import { CustomLink } from '@/components/CustomLink';
import { URL } from '@/constants/application-urls';
import { usePermissions } from '@/utils/hooks/usePermissions';
import { signOut } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
import styles from './UserMenu.module.css';
import { UserIcon, ArrowRightOnRectangleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import LanguageSwitch from '@/components/LanguageSwitch/LanguageSwitch';
import Permissions from '@/enums/Permissions';
import useLanguage from '@/utils/hooks/useLanguage';

export interface UserMenuProps {
	dict: any;
}

export function UserMenu({ dict }: UserMenuProps) {
	const { currentLocale } = useLanguage();
	const ref = useRef<HTMLDivElement>(null);
	const [toggle, setToggle] = useState<boolean>(false);
	const userHasPermission = usePermissions(Permissions.UPDATE_BUSINESS_UNIT_DETAILS);

	useEffect(() => {
		const handleClickOutside = (event: Event) => {
			if (ref.current && event.target instanceof Node && !ref.current.contains(event.target)) {
				setToggle(false);
			}
		};
		if (toggle) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref, toggle]);

	const handleMenuClick = () => {
		setToggle(!toggle);
	};

	return (
		<div className={styles.userMenu} ref={ref}>
			<div className={`${styles.userMenuIcon} ${toggle ? styles.isOpen : ''}`}>
				<UserIcon data-testid="user-menu-icon-toggle" onClick={handleMenuClick} className="flex w-5 h-5" />
			</div>
			<div className={`${styles.userMenuWrapper} ${toggle ? styles.menuOpen : ''}`}>
				<div className={styles.list}>
					<LanguageSwitch placeholder={dict.selectLanguage} />

					{userHasPermission && (
						<CustomLink
							data-testid="user-menu-manage-link"
							href={URL.MANAGE}
							className={`${styles.userMenuItem} ${styles.listItemLink}`}
							onClick={() => setToggle(false)}
						>
							<UserGroupIcon className={'h-6 w-6 mr-4 text-colorPitch'} />
							{dict.manage}
						</CustomLink>
					)}
				</div>
				<div
					data-testid="user-menu-signout-submit"
					className={`${styles.userMenuItem}`}
					onClick={() =>
						signOut({
							callbackUrl: `/${currentLocale}/login`,
						})
					}
				>
					<ArrowRightOnRectangleIcon className="w-6 h-6 mr-4" />

					<span>{dict.logout}</span>
				</div>
			</div>
		</div>
	);
}

export default UserMenu;
